import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import {
  URL_GET_APP_VERSION_BY_PLATFORM,
  VERSIONING_APP_TYPE,
  VERSIONING_PLATFORM_WEB,
} from '@/app/lib/sdk-constants';

/**
 * App-version check against the Versioning service.
 *
 *   POST /api/Versioning/GetAppVersionByPlatform
 *   body: { platform, appType, version }
 *
 * Mirrors Flutter `login_data_source_impl.dart:450-490`.
 *
 * **PascalCase wire response** — same drift pattern as `/api/Master/Relation`
 * + the dental endpoint per memory `feedback_master_relation_pascalcase`.
 * Server returns `{Platform, LatestVersion, ForceUpdateRequired,
 * LogoutRequired, MinSupportVersion, AppType, Message, Key}` (capital
 * first letters). Normalized to camelCase at the hook boundary.
 *
 * Current app version is sourced from `import.meta.env.VITE_APP_VERSION`
 * which Vite replaces at build time with `package.json` `version`.
 * Falls back to `'0.0.0'` in dev or when the env var is missing.
 *
 * Fires on auth boot via `<AppIntegrations />` mounting `<UpgradeAlert />`.
 * Re-runs on window focus / reconnect are disabled per the app-wide
 * QueryClient default — the version-check is a one-time-per-session call.
 */

/**
 * PascalCase wire shape — server returns capital-first-letter keys per
 * Flutter `app_version_model.dart:32-41`. Do not export; consumers see
 * the normalized `AppVersionData` shape instead.
 */
interface AppVersionWire {
  Platform: string | null;
  LatestVersion: string | null;
  ForceUpdateRequired: boolean | null;
  LogoutRequired: boolean | null;
  MinSupportVersion: string | null;
  AppType: string | null;
  Message: string | null;
  Key: string | null;
}

export interface AppVersionData {
  platform: string;
  latestVersion: string;
  forceUpdateRequired: boolean;
  logoutRequired: boolean;
  minSupportVersion: string;
  appType: string;
  message: string;
  key: string;
}

/**
 * Normalize PascalCase wire row → camelCase consumer shape. Exported
 * for direct unit-test coverage per the established pattern from PRs
 * #259/#271. Null wire values collapse to safe defaults (empty string
 * for strings, false for booleans).
 *
 * **Not idempotent by design.** Same Flutter-verbatim brittleness as
 * `fromDentalWire` — if backend ever flips to camelCase, both sides
 * break symmetrically. That's a contract change, not a defensive default.
 */
export function fromAppVersionWire(wire: AppVersionWire): AppVersionData {
  return {
    platform: wire.Platform ?? '',
    latestVersion: wire.LatestVersion ?? '',
    forceUpdateRequired: wire.ForceUpdateRequired ?? false,
    logoutRequired: wire.LogoutRequired ?? false,
    minSupportVersion: wire.MinSupportVersion ?? '',
    appType: wire.AppType ?? '',
    message: wire.Message ?? '',
    key: wire.Key ?? '',
  };
}

/** Read the current bundle version from Vite's env. */
export function getCurrentAppVersion(): string {
  // Vite replaces `import.meta.env.VITE_APP_VERSION` at build time. In
  // dev (no env var set) we fall back to a safe sentinel so the version
  // check still runs without crashing.
  return import.meta.env.VITE_APP_VERSION || '0.0.0';
}

export function useAppVersion() {
  const currentVersion = getCurrentAppVersion();
  return useQuery({
    queryKey: ['app-version', currentVersion],
    queryFn: async ({ signal }) => {
      // Backend signals "no upgrade needed" with `204 No Content`
      // (verified live 2026-05-21). api-client returns `undefined` on
      // empty body — treat as "current" by returning null and skipping
      // the normalizer entirely. The hook consumer (`<UpgradeAlert />`)
      // hides itself when `data` is falsy.
      const wire = await post<AppVersionWire | undefined>(
        URL_GET_APP_VERSION_BY_PLATFORM,
        {
          platform: VERSIONING_PLATFORM_WEB,
          appType: VERSIONING_APP_TYPE,
          version: currentVersion,
        },
        { signal },
      );
      if (!wire) return null;
      return fromAppVersionWire(wire);
    },
    // Skip global error toast — a failed version check should be silent;
    // the upgrade alert just doesn't appear. The user shouldn't see a
    // "Something went wrong" toast on every page load if the endpoint
    // is flaky.
    meta: { skipGlobalError: true },
  });
}

/**
 * Compare two semver-ish version strings. Returns negative if a < b,
 * 0 if equal, positive if a > b. Treats missing/short segments as 0.
 *
 * Mirrors the lexicographic-by-segment comparison Flutter does
 * implicitly when checking `currentVersion < latestVersion`.
 */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
